import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, ButtonBase } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Detector } from 'react-detect-offline';
import classNames from 'classnames';
import RentleLogo from 'common/components/RentleLogo';
import useCurrentTime from 'common/hooks/useCurrentTime';
import useShopFormat from 'hooks/useShopFormat';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';
import * as ViewActions from 'actions/ViewActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import HasFeature from 'components/features/HasFeature';
import { RiMapPinLine, RiSettings4Line, RiWifiLine, RiWifiOffLine } from 'react-icons/ri';

const HeaderStatusBar = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [time] = useCurrentTime();

	const locations = useSelector(ShopSelectors.activeShopAllLocations);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const activeLocation = locations.find((l) => l.id === activeLocationId);

	const [isChecking, setIsChecking] = useState<boolean>(false);
	const { localFormat } = useShopFormat();

	const { t } = useTranslation();

	/** This is mainly to just give the user a feeling like they're doing something.
	 *  In reality it's always polling the online status.
	 */
	const startCheck = useCallback(() => {
		setIsChecking(true);
		setTimeout(() => {
			setIsChecking(false);
		}, 5000);
	}, []);

	const pollingConfig = {
		url: `/online-check.png?id=${newFirestoreId()}`,
		interval: 10000,
		timeout: 10000,
	};

	return (
		<Detector
			polling={pollingConfig}
			render={({ online }: { online: boolean }) => (
				<Box className={classes.wrapper}>
					<Box className={classes.left}>
						<Typography variant="caption" className={classes.text}>
							<RiMapPinLine size={18} />{' '}
							<span style={{ marginLeft: '3px' }} className={classes.textEllipsis}>
								{activeLocation?.name}
							</span>
						</Typography>
					</Box>
					<Box className={classes.middle}>
						<RentleLogo width={55} invert opacity={0.5} type="black" />
					</Box>
					<Box className={classes.right}>
						<HasFeature requiredFeature="DEV">
							<Box
								mr={1}
								className={classes.text}
								onClick={() => dispatch(ViewActions.setDevPanelOpen(true))}
							>
								<RiSettings4Line size={18} />
							</Box>
						</HasFeature>

						{online ? (
							<Typography className={classes.text} variant="caption">
								<RiWifiLine size={18} />
								<span style={{ marginLeft: '3px' }}>{t('common:statusBar.online', 'Online')}</span>
							</Typography>
						) : (
							<Typography className={classes.text} variant="caption">
								<RiWifiOffLine size={18} />
								<span style={{ marginLeft: '3px' }}>
									{t('common:statusBar.offline', 'Offline')}
								</span>
							</Typography>
						)}
						<Box mr={3} />
						<Typography className={classes.text} variant="caption">
							{localFormat(moment(time, 'HH:mm'), 'HH:mm')}
						</Typography>
					</Box>
					<Box className={classes.offlineBadgeContainer}>
						<ButtonBase
							onClick={startCheck}
							className={classNames(classes.offlineBadgeWrapper, {
								[classes.offlineBadgeWrapperVisible]: !online,
							})}
						>
							{isChecking ? (
								<Typography className={classes.offlineBadgeText} variant="caption">
									{t('common:statusBar.checkingConnection', 'Checking connection')}
									{'...'}
								</Typography>
							) : (
								<>
									<Typography className={classes.offlineBadgeText} variant="caption">
										{t('common:statusBar.notConnected', 'Not connected')}
									</Typography>
									<RefreshIcon className={classes.offlineBadgeIcon} />
								</>
							)}
						</ButtonBase>
					</Box>
				</Box>
			)}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			height: '32px',
			background: theme.palette.common.black,
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
			position: 'sticky',
			top: 0,
			zIndex: theme.zIndex.appBar + 2,
		},
		left: {
			flex: 1,
			zIndex: 1103,
			background: theme.palette.common.black,
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			[theme.breakpoints.down('lg')]: {
				maxWidth: '50%',
			},
		},
		middle: {
			flex: 1,
			display: 'none',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			[theme.breakpoints.up('lg')]: {
				display: 'flex',
			},
			zIndex: 1103,
			background: theme.palette.common.black,
		},
		right: {
			flex: 1,
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'flex-end',
			zIndex: 1103,
			background: theme.palette.common.black,
		},
		text: {
			fontSize: '12px',
			color: theme.palette.common.white,
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			maxWidth: '100%',
		},
		textEllipsis: {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: '100%',
			display: 'block',
		},
		offlineBadgeContainer: {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			background: theme.palette.common.black,
			zIndex: 1102,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
		},
		offlineBadgeWrapper: {
			background: theme.palette.warning.light,
			borderColor: theme.palette.warning.main,
			borderWidth: '2px',
			borderStyle: 'solid',
			borderRadius: '2px',
			boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.2)',
			width: '150px',
			height: '31px',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			position: 'absolute',
			top: '-50px',
			zIndex: 1101,
			opacity: 0,
			transition: 'transform 0.3s ease',
		},
		offlineBadgeWrapperVisible: {
			transform: 'translate3d(0, 100px, 0)',
			opacity: 1,
		},
		offlineBadgeText: {
			color: theme.palette.text.primary,
		},
		offlineBadgeIcon: {
			fontSize: '1.5rem',
			marginLeft: theme.spacing(1),
		},
		statusIcon: {
			marginRight: theme.spacing(0.5),
		},
	}),
);

export default HeaderStatusBar;
